import { useState } from 'react';
import ReportsNav from '../../components/ReportsNav';
import {
  assetReportsApi,
  type AssetRegisterDto,
  type DepreciationScheduleRow,
  type AssetMovementRow,
} from '../../api/assetReports';
import MoneyCell from '../../components/MoneyCell';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useReportScreen } from '../../components/ScreenContext';

type Tab = 'register' | 'schedule' | 'movements';

/**
 * Block 3 — turto ataskaitos: registras, nusidėvėjimo planas, judėjimas —
 * the three period-end artifacts over the EXISTING report endpoints
 * (backend done per ADR-005/006; these pages were API-only until now).
 * Read-only — safe on BOTH planes.
 */
export default function AssetReports() {
  const [tab, setTab] = useState<Tab>('register');
  const [reg, setReg] = useState<AssetRegisterDto | null>(null);
  const [schedule, setSchedule] = useState<DepreciationScheduleRow[] | null>(null);
  const [movements, setMovements] = useState<AssetMovementRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async (which: Tab) => {
    setTab(which);
    setLoading(true);
    setError('');
    try {
      if (which === 'register') setReg(await assetReportsApi.register());
      else if (which === 'schedule') setSchedule(await assetReportsApi.depreciationSchedule());
      else setMovements(await assetReportsApi.movements());
    } catch (e: any) {
      setError(e.message || 'Nepavyko gauti ataskaitos');
    } finally {
      setLoading(false);
    }
  };

  const recordCount =
    tab === 'register' ? reg?.assets.length ?? 0
    : tab === 'schedule' ? schedule?.length ?? 0
    : movements?.length ?? 0;

  useReportScreen(`Turto ataskaitos (${tab}): ${recordCount} eilutės`, { recordCount });

  const tabs: { key: Tab; label: string }[] = [
    { key: 'register', label: 'Turto registras' },
    { key: 'schedule', label: 'Nusidėvėjimo planas' },
    { key: 'movements', label: 'Turto judėjimas' },
  ];

  return (
    <div className="space-y-4">
      <ReportsNav />
      <h1>Turto ataskaitos</h1>

      <div className="flex flex-wrap gap-1">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => load(t.key)}
            className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
              tab === t.key
                ? 'bg-primary-50 border-primary-300 text-primary-700'
                : 'bg-white border-gray-200 text-gray-600 hover:border-gray-400'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <div className="error-message">{error}</div>}
      {loading && <LoadingSpinner />}

      {tab === 'register' && reg && (
        <div className="card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Kodas</th>
                <th>Pavadinimas</th>
                <th>Būsena</th>
                <th style={{ textAlign: 'right' }}>Įsigijimo kaina</th>
                <th style={{ textAlign: 'right' }}>Nusidėvėjimas</th>
                <th style={{ textAlign: 'right' }}>Likutinė vertė</th>
                <th style={{ textAlign: 'right' }}>Naud. laikas (mėn.)</th>
              </tr>
            </thead>
            <tbody>
              {reg.assets.map(a => (
                <tr key={a.id}>
                  <td>{a.code ?? ''}</td>
                  <td>{a.name}</td>
                  <td>
                    {a.status === 'Active' ? 'Aktyvus' : a.status === 'WrittenOff' ? 'Nurašytas' : 'Parduotas'}
                    {a.usageSuspendedSince ? ' (pauzė)' : ''}
                  </td>
                  <td style={{ textAlign: 'right' }}><MoneyCell value={a.acquisitionCost} /></td>
                  <td style={{ textAlign: 'right' }}><MoneyCell value={a.accumulatedDepreciation} /></td>
                  <td style={{ textAlign: 'right' }}><strong><MoneyCell value={a.netBookValue} /></strong></td>
                  <td style={{ textAlign: 'right' }}>{a.usefulLifeMonths}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="total-row">
                <td colSpan={3}><strong>Viso ({reg.totals.activeCount} aktyvūs iš {reg.totals.count})</strong></td>
                <td style={{ textAlign: 'right' }}><strong><MoneyCell value={reg.totals.acquisitionCost} /></strong></td>
                <td style={{ textAlign: 'right' }}><strong><MoneyCell value={reg.totals.accumulatedDepreciation} /></strong></td>
                <td style={{ textAlign: 'right' }}><strong><MoneyCell value={reg.totals.netBookValue} /></strong></td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {tab === 'schedule' && schedule && (
        <div className="card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Kodas</th>
                <th>Pavadinimas</th>
                <th style={{ textAlign: 'right' }}>Mėnesio nusidėvėjimas</th>
                <th style={{ textAlign: 'right' }}>Liko nusidėvėti</th>
                <th style={{ textAlign: 'right' }}>Mėnesių liko</th>
              </tr>
            </thead>
            <tbody>
              {schedule.map(a => (
                <tr key={a.id}>
                  <td>{a.code ?? ''}</td>
                  <td>{a.name}{a.suspended ? ' (pauzė)' : ''}</td>
                  <td style={{ textAlign: 'right' }}><MoneyCell value={a.monthlyDepreciation} /></td>
                  <td style={{ textAlign: 'right' }}><MoneyCell value={a.remainingAmount} /></td>
                  <td style={{ textAlign: 'right' }}>{a.monthsLeft}</td>
                </tr>
              ))}
              {schedule.length === 0 && (
                <tr><td colSpan={5} className="text-center text-gray-500">Aktyvių nusidėvėjančių turtų nėra</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'movements' && movements && (
        <div className="card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Turtas</th>
                <th>Operacija</th>
                <th style={{ textAlign: 'right' }}>Suma</th>
                <th>JE</th>
                <th>Aprašymas</th>
              </tr>
            </thead>
            <tbody>
              {movements.map(m => (
                <tr key={m.id}>
                  <td>{m.operationDate.slice(0, 10)}</td>
                  <td>{m.assetName}</td>
                  <td>{m.operationType}</td>
                  <td style={{ textAlign: 'right' }}><MoneyCell value={m.amount} /></td>
                  <td>{m.journalEntryId ?? '—'}</td>
                  <td className="text-xs text-gray-500">{m.calculationDescription ?? ''}</td>
                </tr>
              ))}
              {movements.length === 0 && (
                <tr><td colSpan={6} className="text-center text-gray-500">Operacijų nėra</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
